import { Injector } from '@angular/core';
import {
  SetFormActionWithId,
  UpdateFormDataActionWithId,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import {
  DirectorRecord,
  getDirectorsFromState,
  removeDirectorAt,
  startAddDirector,
} from '@app/finxone-web-frontend/app/lib/utils/companies-house-directors-list.utils';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  buildNestedPatch,
  getNestedValue,
  isBlankValue,
} from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ButtonActionType,
  CompaniesHouseOfficer,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { handleRedirection } from '../cta-redirection.utils';
import { WorkflowFormGuard } from '../../../services/workflow-forms/workflow-form.guard';

// "members" is the committed directors array on businessOnboardingFlow (built up by the add/edit
// wizard's Form Widgets, one array item per director) - distinct from officers.items, which stays
// the raw, untouched Companies House officer list. Override via widgetProperties['rootFormID']/
// ['childKey'] if a project needs a different form/array path.
function getRootFormID(widgetProperties: BaseWidgetProperties): string {
  return (widgetProperties?.['rootFormID'] as string) || FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW;
}

function getChildKey(widgetProperties: BaseWidgetProperties): string {
  return (widgetProperties?.['childKey'] as string) || 'members';
}

/**
 * True only when `director`'s own `director` id exactly matches primaryOfficer.director - the one
 * unambiguous signal. Deliberately does NOT fall back to the director's `isPrimary` flag (it can be
 * stale/unset on a slot the officer-selection widget hasn't re-derived yet) or to matching the
 * "officer-<index>" position (primaryOfficer.director can be a stale index left over from an
 * earlier members-array order) - both misfire on a non-primary director and were seen corrupting
 * primaryOfficer with that director's own (different/blank) fields, e.g. resetting its dob.
 */
function isPrimaryDirector(
  director: DirectorRecord,
  primaryOfficer: Record<string, unknown> | undefined,
): boolean {
  const primaryOfficerId = primaryOfficer?.['director'];
  return (
    !!primaryOfficerId && director['director'] !== undefined && director['director'] === primaryOfficerId
  );
}

/**
 * Derives an ISO (YYYY-MM-DD) date string for a director's `dob` from whichever "Date of birth:
 * Day/Month/Year" fields the wizard's date-type input widget actually populates -
 * officerBirthDay/officerBirthMonth/officerBirthYear (officerBirthMonth is stored 0-indexed, see
 * mapOfficerToFormFields) - falling back to Companies House's own raw dateOfBirth.month/year
 * (1-indexed, no day of birth available) when those aren't present. Nothing else in this codebase
 * ever writes `dob` itself for a member (only primaryOfficer gets one, via normalisePrimaryOfficer)
 * - without this it stays permanently blank, which is also why syncPrimaryOfficerFromDirector below
 * could never give primaryOfficer a real dob for any director, primary or not.
 */
export function deriveDobFromBirthFields(director: DirectorRecord): string | undefined {
  const dateOfBirth = director['dateOfBirth'] as { month: string; year: string } | undefined;

  const year = Number(director['officerBirthYear'] ?? dateOfBirth?.year);
  if (!year) return undefined;

  const hasOfficerBirthMonth =
    director['officerBirthMonth'] !== undefined && director['officerBirthMonth'] !== '';
  const month = hasOfficerBirthMonth
    ? Number(director['officerBirthMonth'])
    : dateOfBirth?.month !== undefined
      ? Number(dateOfBirth.month) - 1 // CH's month is 1-indexed; officerBirthMonth is already 0-indexed
      : NaN;
  if (Number.isNaN(month)) return undefined;

  const day = Number(director['officerBirthDay'] ?? 1);
  if (Number.isNaN(day)) return undefined;

  const date = new Date(Date.UTC(year, month, day));
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
}

/**
 * If the director just saved at `index` has no real `dob` yet, derives one from whichever
 * birth-date fields are actually populated (see deriveDobFromBirthFields) and persists it back
 * onto members[index] - both so the director's own record is correct, and so
 * syncPrimaryOfficerFromDirector (which reads this same record right after) has a real value to
 * propagate instead of an always-blank one.
 */
function backfillDirectorDob(store: Store, rootFormID: string, childKey: string, index: number): void {
  const currentFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(rootFormID),
  )?.formData;
  const directors = getNestedValue<DirectorRecord[]>(currentFormData, childKey) ?? [];
  const director = directors[index];
  if (!director || !isBlankValue(director['dob'])) return;

  const derivedDob = deriveDobFromBirthFields(director);
  if (!derivedDob) return;

  const updatedDirectors = [...directors];
  updatedDirectors[index] = { ...director, dob: derivedDob };
  store.dispatch(
    new SetFormActionWithId(
      { type: '', formData: buildNestedPatch(currentFormData, childKey, updatedDirectors) },
      rootFormID,
    ),
  );
}

/**
 * If the director just saved at `index` is the primary officer, folds its freshly-saved fields
 * back into businessOnboardingFlow.primaryOfficer so pages bound to primaryOfficer directly don't
 * go stale relative to edits made through this director wizard. Deliberately a plain shallow
 * merge (director's own values win per key), not fillBlanks/a deep merge - a director field could
 * be a Date instance or similar (no own enumerable properties of its own), and fillBlanks's
 * `{...value}` recursion into it would silently collapse it to `{}` instead of preserving it.
 */
function syncPrimaryOfficerFromDirector(
  store: Store,
  rootFormID: string,
  childKey: string,
  index: number,
): void {
  const currentFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(rootFormID),
  )?.formData;
  const director = getDirectorsFromState(store, rootFormID, childKey)[index];
  if (!director) return;

  const primaryOfficer = getNestedValue<Record<string, unknown>>(currentFormData, 'primaryOfficer');
  if (!isPrimaryDirector(director, primaryOfficer)) return;

  const updatedPrimaryOfficer = { ...primaryOfficer, ...director };
  store.dispatch(
    new SetFormActionWithId(
      { type: '', formData: buildNestedPatch(currentFormData, 'primaryOfficer', updatedPrimaryOfficer) },
      rootFormID,
    ),
  );
}

/**
 * "Add a new director" - pins the array-edit-index to null so the wizard's first Form Widget
 * page allocates a fresh slot (current array length) when it mounts, then navigates per the
 * button's configured Success outcome (typically the first wizard page).
 */
export function addDirectorAction(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const workflowFormsService = injector.get(WorkflowFormsService);
  startAddDirector(workflowFormsService, getRootFormID(widgetProperties), getChildKey(widgetProperties));
  handleRedirection(
    widgetProperties,
    injector,
    ActionButtonOutcomesType.SUCCESS,
    false,
    false,
    ButtonActionType.ADD_DIRECTOR,
  );
}

/**
 * "Save the director" - the wizard's Form Widgets already dispatch each field's value into
 * businessOnboardingFlow.members as the user types (see FormWidgetComponent's array-type
 * dispatch), so there's no separate commit step needed beyond backfilling dob (see
 * backfillDirectorDob) and syncing the primary officer (see syncPrimaryOfficerFromDirector). This
 * then clears the pinned array-edit-index (so a later "Add" doesn't reuse this director's slot)
 * and navigates per the Success outcome (typically back to the directors list).
 */
export function saveDirectorAction(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const store = injector.get(Store);
  const rootFormID = getRootFormID(widgetProperties);
  const childKey = getChildKey(widgetProperties);
  const arrayStateKey = `${rootFormID}.${childKey}`;

  const currentPageFormValid = workflowFormsService.currentPageFormsValid;
  if (!currentPageFormValid) {
    return;
  }
  const index = workflowFormsService.getActiveFormArrayIndex(arrayStateKey);
  if (index !== null) {
    backfillDirectorDob(store, rootFormID, childKey, index);
    syncPrimaryOfficerFromDirector(store, rootFormID, childKey, index);
  }

  workflowFormsService.setActiveFormArrayIndex(arrayStateKey, null);
  handleRedirection(
    widgetProperties,
    injector,
    ActionButtonOutcomesType.SUCCESS,
    false,
    false,
    ButtonActionType.SAVE_DIRECTOR,
  );
}

/**
 * This action saves the primary director's info in members array and navigates to the success outcome.
 * This manual mapping is needed because the primary officer info is stored in a separate field
 * and needs to be merged with the members array. Without this mapping the user won't be able to submit
 * the form successfully.
 */
export function savePrimaryDirectorInfoAction(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  const rootFormID = getRootFormID(widgetProperties);
  const childKey = getChildKey(widgetProperties);
  const formData = store.selectSnapshot(FormActionState.getFormActionStateWithId(rootFormID))?.formData;
  const primaryOfficerInfo = formData?.primaryOfficer;
  const primaryOfficerUniqueId = primaryOfficerInfo?.uniqueId;
  const memberIndex = formData?.[childKey]?.findIndex(
    (member: CompaniesHouseOfficer) => member.uniqueId === primaryOfficerUniqueId,
  );
  if (memberIndex !== -1) {
    formData[childKey][memberIndex] = {
      ...formData[childKey][memberIndex],
      ...primaryOfficerInfo,
    };
    store.dispatch(new UpdateFormDataActionWithId({ formData }, rootFormID));
    backfillDirectorDob(store, rootFormID, childKey, memberIndex);
  } else {
    console.error('Primary officer not found in members');
  }
  handleRedirection(
    widgetProperties,
    injector,
    ActionButtonOutcomesType.SUCCESS,
    false,
    false,
    ButtonActionType.SAVE_PRIMARY_DIRECTOR_INFO,
  );
}

/**
 * "Remove the director" - removes whichever director is currently pinned via the array-edit-index
 * (set by an earlier Edit/Add trigger before navigating into the wizard), then clears the pin and
 * navigates per the Success outcome. Intended for a "Delete this director" button on the wizard/edit
 * context itself, not a per-row button on the list (there's no per-row index plumbing for CTA
 * buttons in this codebase - see startEditDirector for how a list row would pin an index first).
 */
export function removeDirectorAction(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const store = injector.get(Store);
  const rootFormID = getRootFormID(widgetProperties);
  const childKey = getChildKey(widgetProperties);
  const arrayStateKey = `${rootFormID}.${childKey}`;

  const index = workflowFormsService.getActiveFormArrayIndex(arrayStateKey);
  if (index !== null) {
    removeDirectorAt(store, rootFormID, childKey, index);
    workflowFormsService.setActiveFormArrayIndex(arrayStateKey, null);
  }

  // Disable form validation check for navigating to the set page
  WorkflowFormGuard.disableFormValidation();
  handleRedirection(
    widgetProperties,
    injector,
    ActionButtonOutcomesType.SUCCESS,
    false,
    false,
    ButtonActionType.REMOVE_DIRECTOR,
  );
}
